import React, { useState, useMemo } from 'react';
import { Button, Modal, Form, Spinner, Alert, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import GLOBALVAR from '../../constants';
import "./css/ApproveButton.scss";

// Move LoanDetailsModal outside of the component to prevent re-initialization
const LoanDetailsModal = ({ 
    show, 
    onHide, 
    loading, 
    error, 
    setError, // Add setError to props
    loanDetails, 
    setLoanDetails, 
    calculatedFees, 
    onSubmit 
}) => (
    <Modal
        show={show}
        onHide={onHide}
        centered
        backdrop="static"
        size="lg"
    >
        <Modal.Header closeButton={!loading}>
            <Modal.Title>Loan Processing Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            <Form>
                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Loan Processing Type</Form.Label>
                            <Form.Select
                                value={loanDetails.loanProcessingType}
                                onChange={(e) => setLoanDetails({
                                    ...loanDetails,
                                    loanProcessingType: e.target.value
                                })}
                                disabled={loading}
                            >
                                <option value="">Select Type</option>
                                <option value="new">New Loan</option>
                                <option value="reloan">Reloan</option>
                                <option value="restructure">Restructure</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Payment Type</Form.Label>
                            <Form.Select
                                value={loanDetails.paymentType}
                                onChange={(e) => setLoanDetails({
                                    ...loanDetails,
                                    paymentType: e.target.value
                                })}
                                disabled={loading}
                            >
                                <option value="">Select Type</option>
                                <option value="add">Add On</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col>
                        <Form.Group>
                            <Form.Label>Loan Interval</Form.Label>
                            <Form.Select
                                value={loanDetails.loanInterval}
                                onChange={(e) => setLoanDetails({
                                    ...loanDetails,
                                    loanInterval: e.target.value
                                })}
                                disabled={loading}
                            >
                                <option value="">Select Interval</option>
                                <option value="monthly">Monthly</option>
                                <option value="bimonthly">Bi-Monthly</option>
                                <option value="weekly">Weekly</option>
                                <option value="quarterly">Quarterly</option>
                                <option value="semiannual">Semi-Annual</option>
                                <option value="yearly">Yearly</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col>
                        <Form.Group>
                            <Form.Label>Interest Rate (%)</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                min="0"
                                value={loanDetails.interestRate}
                                onChange={(e) => setLoanDetails({
                                    ...loanDetails,
                                    interestRate: e.target.value
                                })}
                                disabled={loading}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                {/* Display calculated fees in readonly mode */}
                <Row className="mb-3">
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label>Service Fee</Form.Label>
                            <Form.Control
                                type="number"
                                value={calculatedFees.serviceFee}
                                readOnly
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label>Notarial Fee</Form.Label>
                            <Form.Control
                                type="number"
                                value={calculatedFees.notarialFee}
                                readOnly
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label>Doc Stamp</Form.Label>
                            <Form.Control
                                type="number"
                                value={calculatedFees.docStamp}
                                readOnly
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label>Insurance</Form.Label>
                            <Form.Control
                                type="number"
                                value={calculatedFees.insurance}
                                readOnly
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Account Officer</Form.Label>
                            <Form.Control
                                type="text"
                                value={loanDetails.accountOfficer}
                                onChange={(e) => setLoanDetails({
                                    ...loanDetails,
                                    accountOfficer: e.target.value
                                })}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Loan Product</Form.Label>
                            <Form.Select
                                value={loanDetails.loanProduct}
                                onChange={(e) => setLoanDetails({
                                    ...loanDetails,
                                    loanProduct: e.target.value
                                })}
                            >
                                <option value="">Select Product</option>
                                <option value="regular">Regular Loan</option>
                                <option value="salary">Salary Loan</option>
                                <option value="pension">Pension Loan</option>
                                <option value="honorarium">Honorarium Loan</option>
                                <option value="market">Market Vendors</option>
                                <option value="clean">Clean Loan</option>
                                <option value="chattel">Chattel</option>
                                <option value="rem">REM</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Loan Ledger</Form.Label>
                            <Form.Control
                                type="text"
                                value={loanDetails.loanLedger}
                                onChange={(e) => setLoanDetails({
                                    ...loanDetails,
                                    loanLedger: e.target.value
                                })}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Loan Security</Form.Label>
                            <Form.Select
                                value={loanDetails.loanSecurity}
                                onChange={(e) => setLoanDetails({
                                    ...loanDetails,
                                    loanSecurity: e.target.value
                                })}
                            >
                                <option value="">Select Security</option>
                                <option value="unsecured">Unsecured</option>
                                <option value="atm">Secured with ATM</option>
                                <option value="pdc">Secured with PDC</option>
                                <option value="clean">Clean Loan</option>
                                <option value="chattel">Chattel Mortgage</option>
                                <option value="residential">Real Estate Mortgage - Residential</option>
                                <option value="commercial">Real Estate Mortgage - Commercial</option>
                                <option value="others">Others</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col>
                        <Form.Group>
                            <Form.Label>Loan Type</Form.Label>
                            <Form.Select
                                value={loanDetails.loanType}
                                onChange={(e) => setLoanDetails({
                                    ...loanDetails,
                                    loanType: e.target.value
                                })}
                            >
                                <option value="">Select Type</option>
                                <option value="personal">Personal Loan</option>
                                <option value="business">Business Loan</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                <div className="d-grid gap-2 mt-4">
                    <Button
                        variant="primary"
                        onClick={onSubmit}
                        disabled={loading}
                    >
                        {loading ? (
                            <Spinner animation="border" size="sm" className="me-2" />
                        ) : null}
                        Create Loan Processing
                    </Button>
                </div>
            </Form>
        </Modal.Body>
    </Modal>
);

const ApproveButton = ({ formData, onUpdate, onSuccess, isCompact }) => {
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showLoanDetailsModal, setShowLoanDetailsModal] = useState(false);
    const [loanDetails, setLoanDetails] = useState({
        loanProcessingType: '',
        paymentType: '',
        loanInterval: '',
        serviceFee: '',
        notarialFee: '',
        docStamp: '',
        insurance: '',
        accountOfficer: '',
        loanProduct: '',
        loanLedger: '',
        loanSecurity: '',
        loanType: '',
        interestRate: '5' // Default to 5%
    });

    // Add useMemo to calculate fees
    const calculatedFees = useMemo(() => {
        const amountGranted = parseFloat(formData.loanAmount || 0);
        return {
            serviceFee: 125, // 100 service fee + 25 processing fee
            notarialFee: 300,
            docStamp: parseFloat((amountGranted / 200 * 1.5).toFixed(2)),
            insurance: parseFloat((amountGranted / 1000 * 10).toFixed(2))
        };
    }, [formData.loanAmount]);

    // Search applications
    const searchApplications = async (query) => {
        try {
            const response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/applicationsearch`, {
                params: { query }
            });
            setSuggestions(response.data.rows);
        } catch (error) {
            console.error('Search error:', error);
            setError('Failed to search applications');
        }
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        if (query.length > 2) {
            searchApplications(query);
        } else {
            setSuggestions([]);
        }
    };

    // Handle application selection
    const handleSelectApplication = (application) => {
        setSelectedApplication(application);
        setSearchQuery(`${application.firstName} ${application.lastName}`);
        setSuggestions([]);
    };

    const mapFormDataForApplication = (formData) => {
        return {
            // Personal Information (Required)
            lastName: formData.lastName,           // Required
            firstName: formData.firstName,         // Required
            middleName: formData.middleName,      // Required
            mothersMaidenName: formData.motherMaidenName, // Required 
            
            // Address (Required)
            street: formData.houseStreet,         // Required
            barangay: formData.barangay,          // Required
            municipality: formData.municipality,   // Required
            province: formData.province,          // Required
            zipCode: formData.zipCode,            // Required
            
            // Personal Details (Required)
            birthday: formData.birthday,          // Required
            gender: formData.gender,              // Required
            civilStatus: formData.civilStatus,    // Required
            
            // Spouse Information (Required if Married)
            marriedTo: formData.civilStatus === 'Married' ? formData.spouseName : '',
            
            // Contact Information (At least one required)
            telephoneNo: formData.civilStatus === 'Married' ? formData.spouseContact : '',
            mobileNo1: formData.mobileNumber1,    // Required
            mobileNo2: formData.mobileNumber2 || '', // Optional
            emailAddress: formData.email,         // Required
            
            // Government IDs (Optional)
            sssOrGsis: '',                        // Optional
            passport: '',                         // Optional
            
            // Employment Information (Required)
            employerDetails: `${formData.employer || ''} - ${formData.natureOfWork || ''}`, // Required
            employerAddress: '',                  // Optional
            employerContactNumber: '',            // Optional
            employerLocation: formData.jobPosition || '',                 // Optional
            
            // Additional Fields (if needed by your backend)
            sourceOfIncome: formData.sourceOfIncome,
            monthlyIncome: formData.monthlyIncome,
            natureOfWork: formData.natureOfWork
        };
    };

    const validateRequiredFields = (mappedData) => {
        const required = [
            'lastName',
            'firstName',
            'middleName',
            'mothersMaidenName',
            'street',
            'barangay',
            'municipality',
            'province',
            'zipCode',
            'birthday',
            'gender',
            'civilStatus',
            'mobileNo1',
            'emailAddress',
            'employerDetails'
        ];

        const missing = required.filter(field => !mappedData[field]);
        
        if (mappedData.civilStatus === 'Married' && !mappedData.marriedTo) {
            missing.push('marriedTo');
        }

        return missing;
    };

    const validateLoanDetails = (details) => {
        const required = [
            'loanProcessingType',
            'paymentType',
            'loanInterval',
            'serviceFee',
            'notarialFee',
            'docStamp',
            'insurance',
            'accountOfficer',
            'loanProduct',
            'loanLedger',
            'loanSecurity',
            'loanType'
        ];

        return required.filter(field => !details[field]);
    };

    // Modify handleNewApplication
    const handleNewApplication = async () => {
        try {
            setLoading(true);
            setError(null);

            const mappedData = mapFormDataForApplication(formData);
            const missingFields = validateRequiredFields(mappedData);

            if (missingFields.length > 0) {
                setError(`Missing required fields: ${missingFields.join(', ')}`);
                return;
            }

            const applicationResponse = await axios.post(
                `${GLOBALVAR['SERVER_ROOT']}/api/applications/add`,
                mappedData
            );

            // Update loanDetails with calculated fees
            setLoanDetails(prevState => ({
                ...prevState,
                ...calculatedFees
            }));

            setSelectedApplication(applicationResponse.data.application);
            setShowModal(false);
            setShowLoanDetailsModal(true);
            setLoading(false); // Important: Reset loading state here

        } catch (error) {
            setError(error.response?.data?.message || 'Failed to process application');
            setLoading(false);
        }
    };

    const handleExistingApplication = async () => {
        try {
            setLoading(true);
            setError(null);

            if (!selectedApplication) {
                setError('Please select an existing application first');
                setLoading(false);
                return;
            }

            // Map form data just like in handleNewApplication
            const mappedData = mapFormDataForApplication(formData);
            const missingFields = validateRequiredFields(mappedData);

            if (missingFields.length > 0) {
                setError(`Missing required fields: ${missingFields.join(', ')}`);
                setLoading(false);
                return;
            }

            // Update the existing application with the new data
            const applicationResponse = await axios.post(
                `${GLOBALVAR['SERVER_ROOT']}/api/applications/update/${selectedApplication.id}`,
                mappedData
            );

            // Update loanDetails with calculated fees
            setLoanDetails(prevState => ({
                ...prevState,
                ...calculatedFees
            }));

            // Update the selectedApplication with the updated data
            setSelectedApplication(applicationResponse.data.application);
            
            // Close the search modal and show the loan details modal
            setShowModal(false);
            setShowLoanDetailsModal(true);
            
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to update application');
        } finally {
            setLoading(false);
        }
    };

    const handleLoanProcessingSubmit = async () => {
        try {
            setLoading(true);
            setError(null);

            const missingFields = validateLoanDetails({
                ...loanDetails,
                ...calculatedFees
            });

            if (missingFields.length > 0) {
                setError(`Missing required fields: ${missingFields.join(', ')}`);
                setLoading(false);
                return;
            }

            // First create loan processing
            const loanProcessingData = {
                applicantID: selectedApplication.id,
                amountGranted: formData.loanAmount,
                dateGranted: new Date(),
                interestRate: formData.interestRate,
                loanTerm: formData.loanTerm,
                advanceInterest: true,
                reference: formData.referredBy,
                ...loanDetails,
                ...calculatedFees
            };

            await axios.post(
                `${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/add`,
                loanProcessingData
            );

            // If loan processing creation is successful, update application status
            await axios.put(
                `${GLOBALVAR['SERVER_ROOT']}/api/loan-application/${formData.id}`,
                {
                    status: 'approved',
                    applicationsId: selectedApplication.id
                }
            );

            // Calculate dates
            const approvalDate = new Date().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: '2-digit'
            });

            // Calculate release date based on current time
            const now = new Date();
            const currentHour = now.getHours();
            const isWeekend = now.getDay() === 0 || now.getDay() === 6;
            const isDuringBusinessHours = currentHour >= 7 && currentHour < 17; // 7 AM to 5 PM

            // Set initial release date
            let releaseDate;
            if (isWeekend || !isDuringBusinessHours) {
                // If weekend or outside business hours, set to next business day
                releaseDate = new Date();
                releaseDate.setDate(releaseDate.getDate() + 1);
                // Skip weekends
                while (releaseDate.getDay() === 0 || releaseDate.getDay() === 6) {
                    releaseDate.setDate(releaseDate.getDate() + 1);
                }
            } else {
                // During business hours on weekday, use current date
                releaseDate = now;
            }

            const formattedReleaseDate = releaseDate.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: '2-digit'
            });

            // Calculate initial interest
            const initialInterest = (parseFloat(formData.loanAmount) * (parseFloat(loanDetails.interestRate) / 100));
            
            // Calculate total deductions
            const totalDeductions = calculatedFees.serviceFee + 
                calculatedFees.notarialFee + 
                calculatedFees.docStamp + 
                calculatedFees.insurance +
                initialInterest;

            // Calculate net proceeds
            const netProceeds = parseFloat(formData.loanAmount) - totalDeductions;

            // Send approval notification emails with net proceeds and total deductions
            await axios.post(`${GLOBALVAR['SERVER_ROOT']}/api/templates/send/loan_approve`, {
                recipients: [
                    {
                        email: formData.email,
                        data: {
                            applicant_name: `${formData.firstName} ${formData.lastName}`,
                            approval_date: approvalDate,
                            loan_amount: `₱${parseFloat(formData.loanAmount).toLocaleString()}`,
                            loan_term: `${formData.loanTerm} months`,
                            comaker_name: `${formData.coMakerFirstName} ${formData.coMakerLastName}`,
                            release_date: formattedReleaseDate,
                            loan_net: `₱${netProceeds.toLocaleString()}`,
                            loan_loandeduct: `₱${totalDeductions.toLocaleString()}`
                        }
                    },
                    {
                        email: formData.coMakerEmail,
                        data: {
                            applicant_name: `${formData.firstName} ${formData.lastName}`,
                            approval_date: approvalDate,
                            loan_amount: `₱${parseFloat(formData.loanAmount).toLocaleString()}`,
                            loan_term: `${formData.loanTerm} months`,
                            comaker_name: `${formData.coMakerFirstName} ${formData.coMakerLastName}`,
                            release_date: formattedReleaseDate,
                            loan_net: `₱${netProceeds.toLocaleString()}`,
                            loan_loandeduct: `₱${totalDeductions.toLocaleString()}`
                        }
                    }
                ]
            });

            // Close modals first
            setShowLoanDetailsModal(false);
            setShowModal(false);

            // Update parent component with new data
            if (typeof onUpdate === 'function') {
                onUpdate({ 
                    ...formData, 
                    status: 'approved',
                    applicationsId: selectedApplication.id 
                });
            }

            // Reset all state
            setLoanDetails({
                loanProcessingType: '',
                paymentType: '',
                loanInterval: '',
                serviceFee: '',
                notarialFee: '',
                docStamp: '',
                insurance: '',
                accountOfficer: '',
                loanProduct: '',
                loanLedger: '',
                loanSecurity: '',
                loanType: '',
                interestRate: '5'
            });
            setSelectedApplication(null);
            setSearchQuery('');
            setSuggestions([]);

            // Show success message through parent component
            if (typeof onSuccess === 'function') {
                onSuccess('Loan processing created and application approved successfully');
            }

        } catch (error) {
            setError(error.response?.data?.message || 'Failed to process application');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button 
                variant="success"
                onClick={() => setShowModal(true)}
                disabled={loading}
                size="sm"
                className="btn-icon-flex"
                title="Approve Application"
            >
                {loading ? (
                    <Spinner animation="border" size="sm" />
                ) : (
                    <>
                        <i className="fas fa-check"></i>
                        {!isCompact && <span className="ms-1">Approve</span>}
                    </>
                )}
            </Button>

            <Modal
                show={showModal}
                onHide={() => !loading && setShowModal(false)}
                centered
                backdrop="static"
                className="approve-modal"
            >
                <Modal.Header closeButton={!loading} className="border-0 pb-0">
                    <Modal.Title className="h5">Approve Loan Application</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                            {success}
                        </Alert>
                    )}

                    <Form.Group className="mb-4">
                        <Form.Label className="fw-medium">Search Existing Applications</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Type name to search..."
                            value={searchQuery}
                            onChange={(e) => {
                                handleSearchChange(e);
                                setSelectedApplication(null);
                            }}
                            disabled={loading}
                        />
                        {suggestions.length > 0 && (
                            <div className="suggestions-dropdown">
                                {suggestions.slice(0, 5).map((app) => (
                                    <div
                                        key={app.id}
                                        className="suggestion-item"
                                        onClick={() => handleSelectApplication(app)}
                                    >
                                        <i className="fas fa-user me-2"></i>
                                        {app.firstName} {app.lastName}
                                    </div>
                                ))}
                            </div>
                        )}
                    </Form.Group>

                    <div className="d-grid gap-2">
                        <Button
                            variant="primary"
                            onClick={handleNewApplication}
                            disabled={loading}
                            className="btn-lg"
                        >
                            {loading ? (
                                <Spinner animation="border" size="sm" className="me-2" />
                            ) : (
                                <i className="fas fa-plus-circle me-2"></i>
                            )}
                            Create New Application & Process Loan
                        </Button>
                        <Button
                            variant="outline-primary"
                            onClick={handleExistingApplication}
                            disabled={loading || !selectedApplication}
                            className="btn-lg"
                        >
                            {loading ? (
                                <Spinner animation="border" size="sm" className="me-2" />
                            ) : (
                                <i className="fas fa-link me-2"></i>
                            )}
                            Process Loan for Selected Application
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            <LoanDetailsModal 
                show={showLoanDetailsModal}
                onHide={() => !loading && setShowLoanDetailsModal(false)}
                loading={loading}
                error={error}
                setError={setError} // Pass setError as prop
                loanDetails={loanDetails}
                setLoanDetails={setLoanDetails}
                calculatedFees={calculatedFees}
                onSubmit={handleLoanProcessingSubmit}
            />
        </>
    );
};

export default ApproveButton;